import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCEPT_ATTRIBUTE,
  ALLOWED_SUMMARY,
  ATTACHABLE_MAP,
  MAX_FILE_BYTES,
  attachmentKind,
  fileTypeLabel,
  formatBytes,
  isAllowed,
} from '@goyone/shared';
import { api, upload as uploadFile } from '../lib/api';
import { dateTime } from '../lib/format';
import { Icon } from './Icon';
import { Modal } from './ui';
import {
  AudioPlayer,
  ImageThumb,
  Lightbox,
  PdfThumb,
  iconForKind,
  useAttachmentUrl,
} from './AttachmentPreview';
import { usePagedNewestFirst } from './Pager';

/* ==========================================================================
   أدوات مشتركة — الرفع بعد الإنشاء، والعدّ لصفحة كاملة، واختيار الملفات
   ========================================================================== */

/**
 * جهاز لمسٍ بكاميرا غالبًا — يظهر عنده زرّ «تصوير».
 *
 * `capture` على الحاسوب يُفتح به اختيار الملفات العادي، فزرّه هناك تكرارٌ
 * لزرّ الإرفاق بلا فائدة.
 */
const COARSE_POINTER =
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

/** الفحص نفسه قبل الرفع — يعيد سبب الرفض أو لا شيء. */
export function fileProblem(file: File): string | null {
  if (file.size > MAX_FILE_BYTES) {
    return `«${file.name}»: حجمه ${formatBytes(file.size)} والحد ${formatBytes(MAX_FILE_BYTES)}.`;
  }
  if (!isAllowed(file.name, file.type)) {
    return `«${file.name}»: نوع غير مقبول. المسموح: ${ALLOWED_SUMMARY}.`;
  }
  return null;
}

/**
 * رفع ملفات على سجلٍ وُلد للتو.
 *
 * النماذج التي تُنشئ السجل وسنده معًا (التحصيل، القيد) تحفظ السجل أولًا
 * ثم ترفع عليه — فالمرفق لا يقوم بلا معرّف. وفشل الرفع لا يُبطل السجل:
 * يُعاد سببه ليُعرض، والملف يُرفع لاحقًا من نافذة مرفقاته.
 */
export async function uploadAttachments(
  entity: string,
  entityId: string,
  files: File[],
): Promise<string | null> {
  const failed: string[] = [];
  for (const file of files) {
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('entity', entity);
      form.append('entityId', entityId);
      await uploadFile('/attachments', form);
    } catch (e) {
      failed.push(`«${file.name}»: ${e instanceof Error ? e.message : 'تعذّر الرفع.'}`);
    }
  }
  return failed.length ? failed.join(' ') : null;
}

/**
 * عدد المرفقات لصفحة سجلات في طلب واحد.
 *
 * المفتاح تحت «attachments» فيتجدّد مع كل رفع أو حذف من المكوّن أدناه —
 * وهو يُبطل المفتاح كلّه.
 */
export function useAttachmentCounts(entity: string, ids: string[]): Record<string, number> {
  const key = ids.join(',');
  const counts = useQuery({
    queryKey: ['attachments', 'counts', entity, key],
    queryFn: () =>
      api<Record<string, number>>(`/attachments/counts?entity=${entity}&ids=${key}`),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  return counts.data ?? {};
}

/** زرّ المشبك في صف القائمة — والعدد بجواره حين يوجد مرفق. */
export function AttachmentCountButton({
  count,
  onClick,
  label,
}: {
  count: number;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`btn btn-sm btn-ghost${count ? '' : ' btn-icon'}`}
      onClick={onClick}
      title={count ? `${count} مرفق` : 'إرفاق سند'}
      aria-label={count ? `${label} — ${count} مرفق` : label}
      style={count ? { gap: 4, paddingInline: 6 } : undefined}
    >
      <Icon name="paperclip" size={14} />
      {count > 0 && <span className="num tiny">{count}</span>}
    </button>
  );
}

/**
 * اختيار ملفات قبل الحفظ — للنماذج التي تُنشئ السجل.
 *
 * الملفات تُحفظ في الحالة وتُرفع بعد إنشاء السجل بـ`uploadAttachments`.
 * وعلى الجوال زرّ «تصوير» يفتح الكاميرا مباشرةً: صورة الإيصال تُلتقط في
 * مكانها بدل البحث عنها في المعرض.
 */
export function PendingFiles({
  id,
  files,
  onChange,
  label = 'السند (صورة أو ملف)',
  hint,
}: {
  id: string;
  files: File[];
  onChange: (files: File[]) => void;
  label?: string;
  hint?: string;
}) {
  const [error, setError] = useState<string | null>(null);

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = [...list];
    const bad = picked.map(fileProblem).filter(Boolean) as string[];
    setError(bad.length ? bad.join(' ') : null);
    const ok = picked.filter((f) => !fileProblem(f));
    // الملف نفسه مرتين لا يُضاف — الخادم سيرفض التوأم على أي حال.
    const fresh = ok.filter(
      (f) => !files.some((x) => x.name === f.name && x.size === f.size),
    );
    if (fresh.length) onChange([...files, ...fresh]);
  };

  return (
    <div className="field span-2">
      <span className="label">{label}</span>
      <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <label className="btn btn-sm" htmlFor={`${id}-file`}>
          <Icon name="paperclip" size={14} />
          إرفاق ملف
        </label>
        <input
          id={`${id}-file`}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          style={{ display: 'none' }}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
        {COARSE_POINTER && (
          <>
            <label className="btn btn-sm" htmlFor={`${id}-cam`}>
              <Icon name="camera" size={14} />
              تصوير
            </label>
            <input
              id={`${id}-cam`}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                add(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {files.length > 0 && (
        <ul className="mt-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="row tiny" style={{ gap: 'var(--sp-2)' }}>
              <Icon name="paperclip" size={12} />
              <span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>
              <span className="mute">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                aria-label={`إزالة ${f.name}`}
                title="إزالة"
              >
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error ? (
        <span className="field-error">{error}</span>
      ) : (
        <span className="tiny mute">
          {hint ?? `${ALLOWED_SUMMARY} — حتى ${formatBytes(MAX_FILE_BYTES)} للملف.`}
        </span>
      )}
    </div>
  );
}

interface AttachmentRow {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  note: string | null;
  uploaderName: string | null;
  createdAt: string;
}

/**
 * المرفقات على سجل.
 *
 * مكوّن واحد يُركَّب على أي شاشة سجل: العميل والعقد والفاتورة والموظف
 * والأصل. الكيان يأتي وسيطًا، والخادم يفحصه ضد قائمة مغلقة.
 *
 * وكل نوع يُعامَل بما يناسبه: الصورة تُعرض مصغَّرة وتُكبَّر بنقرة، وPDF
 * يُفتح في عارض المتصفح، والتسجيل يُشغَّل في مكانه. كان الجميع سطرًا نصيًا
 * وزرَّ حذف — فمن يريد أن يرى صورة إيصال يُنزّلها ويفتحها من مجلد التنزيلات
 * ثم يعود، وهو ما لا يفعله أحد مرتين.
 */
export function Attachments({
  entity,
  entityId,
  title = 'المرفقات',
}: {
  entity: string;
  entityId: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<AttachmentRow | null>(null);
  const [removing, setRemoving] = useState<AttachmentRow | null>(null);

  const def = ATTACHABLE_MAP[entity];

  const list = useQuery({
    queryKey: ['attachments', entity, entityId],
    queryFn: () => api<AttachmentRow[]>(`/attachments?entity=${entity}&entityId=${entityId}`),
    enabled: Boolean(entityId),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['attachments'] });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const onPick = async (file: File) => {
    setError(null);

    /*
     * الفحص هنا وفي الخادم معًا.
     *
     * هذا لتوفير رفع عشرة ميجابايت تُرفَض عند وصولها، والخادم هو الحكم:
     * فحص الواجهة يُتجاوَز بطلب مباشر.
     */
    if (file.size > MAX_FILE_BYTES) {
      setError(`حجم الملف ${formatBytes(file.size)} والحد ${formatBytes(MAX_FILE_BYTES)}.`);
      return;
    }
    if (!isAllowed(file.name, file.type)) {
      setError(`نوع الملف غير مقبول. المسموح: ${ALLOWED_SUMMARY}.`);
      return;
    }

    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('entity', entity);
      form.append('entityId', entityId);
      await uploadFile('/attachments', form);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'تعذّر الرفع.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /**
   * التنزيل عبر fetch لا برابط مباشر.
   *
   * الطلب يحتاج ترويسة المستأجر ورمز الوصول، وتنقّل المتصفح العادي لا
   * يحملهما — فيصل المستخدم إلى صفحة رفض بدل ملفه.
   */
  const download = async (row: AttachmentRow) => {
    setError(null);
    try {
      const blob = await api<Blob>(`/attachments/${row.id}/download`, { raw: true });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'تعذّر التنزيل.');
    }
  };

  const rows = list.data ?? [];

  /* الأحدث رفعًا فوق وخمسةٌ في الصفحة — والقديم يُقصد برقم صفحته. */
  const { shown, pager } = usePagedNewestFirst(rows, 5);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2 className="card-title">{title}</h2>
          {def && <p className="tiny mute">{def.hint}</p>}
        </div>

        <span className="row" style={{ gap: 'var(--sp-2)' }}>
          {/* على الجوال: الكاميرا مباشرةً لصورة إيصال أو شيك. */}
          {COARSE_POINTER && !busy && (
            <label className="btn btn-sm" title="تصوير">
              <Icon name="camera" size={14} />
              تصوير
              <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onPick(f);
                  e.target.value = '';
                }}
              />
            </label>
          )}
          <label className={`btn btn-sm${busy ? ' disabled' : ''}`}>
            {busy ? 'جارٍ الرفع' : 'إرفاق ملف'}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPick(f);
              }}
            />
          </label>
        </span>
      </div>

      {error && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="alert alert-danger">{error}</div>
        </div>
      )}

      {list.isPending && (
        <div className="card-body">
          <div className="skeleton" style={{ height: 64 }} />
        </div>
      )}

      {rows.length > 0 ? (
        <div className="card-body att-list">
          {shown.map((r, i) => (
            <AttachmentItem
              key={r.id}
              row={r}
              index={i}
              onView={() => setViewing(r)}
              onDownload={() => void download(r)}
              onRemove={() => setRemoving(r)}
            />
          ))}
          {pager}
        </div>
      ) : (
        !list.isPending && (
          <div className="card-body">
            <p className="mute">لا مرفقات.</p>
            <p className="tiny mute mt-2">
              {ALLOWED_SUMMARY} — حتى {formatBytes(MAX_FILE_BYTES)} للملف.
            </p>
          </div>
        )
      )}

      {viewing && attachmentKind(viewing.mimeType) === 'image' && (
        <Lightbox
          id={viewing.id}
          filename={viewing.filename}
          onClose={() => setViewing(null)}
        />
      )}

      {viewing && attachmentKind(viewing.mimeType) === 'pdf' && (
        <PdfViewer row={viewing} onClose={() => setViewing(null)} />
      )}

      {/*
        الحذف يسأل ولو لم يكن له توابع.
        المرفق لا يُسترجع، ورفعه مرة أخرى يتطلب أن يكون الأصل ما زال عند
        من رفعه — وهو ما لا يُعرف وقت الضغط.
      */}
      {removing && (
        <Modal
          title="حذف مرفق"
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn" onClick={() => setRemoving(null)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(removing.id)}
              >
                {remove.isPending ? 'جارٍ الحذف' : 'حذف نهائي'}
              </button>
            </>
          }
        >
          <p>
            سيُحذف «<strong>{removing.filename}</strong>» نهائيًا، ولا يُسترجع.
          </p>
          <p className="tiny mute mt-2">
            {fileTypeLabel(removing.filename, removing.mimeType)} · {formatBytes(removing.size)}
            {removing.uploaderName && ` · رفعه ${removing.uploaderName}`}
          </p>
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   عنصر المرفق
   ========================================================================== */

function AttachmentItem({
  row,
  index,
  onView,
  onDownload,
  onRemove,
}: {
  row: AttachmentRow;
  index: number;
  onView: () => void;
  onDownload: () => void;
  onRemove: () => void;
}) {
  const kind = attachmentKind(row.mimeType);
  const previewable = kind === 'image' || kind === 'pdf';

  return (
    <div className="att-item reveal" style={{ ['--reveal-i' as string]: index }}>
      {kind === 'image' ? (
        <ImageThumb id={row.id} filename={row.filename} onOpen={onView} />
      ) : kind === 'pdf' ? (
        /* الصفحة الأولى مصغَّرةً لا مربعًا فارغًا — والنقر يفتح المعاينة. */
        <PdfThumb id={row.id} filename={row.filename} onOpen={onView} />
      ) : (
        <span className="att-icon">
          <Icon name={iconForKind(kind)} size={18} />
        </span>
      )}

      <div className="att-body">
        <button
          type="button"
          className="att-name"
          onClick={previewable ? onView : onDownload}
          title={previewable ? 'معاينة' : 'تنزيل'}
        >
          {row.filename}
        </button>

        <div className="tiny mute">
          {fileTypeLabel(row.filename, row.mimeType)} · {formatBytes(row.size)}
          {row.uploaderName && ` · ${row.uploaderName}`}
          {` · ${dateTime(row.createdAt)}`}
        </div>

        {row.note && <div className="tiny mute">{row.note}</div>}

        {kind === 'audio' && (
          <div className="mt-2">
            <AudioPlayer id={row.id} />
          </div>
        )}
      </div>

      <div className="att-actions">
        {previewable && (
          <button
            className="btn btn-icon btn-ghost btn-sm"
            onClick={onView}
            aria-label={`معاينة ${row.filename}`}
            title="معاينة"
          >
            <Icon name="eye" size={14} />
          </button>
        )}
        <button
          className="btn btn-icon btn-ghost btn-sm"
          onClick={onDownload}
          aria-label={`تنزيل ${row.filename}`}
          title="تنزيل"
        >
          <Icon name="download" size={14} />
        </button>
        <button
          className="btn btn-icon btn-ghost btn-sm"
          onClick={onRemove}
          aria-label={`حذف ${row.filename}`}
          title="حذف"
        >
          <Icon name="trash" size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * عارض PDF.
 *
 * `<iframe>` على عنوان blob يستدعي عارض المتصفح نفسه — لا مكتبة PDF في
 * المشروع، وهو المبدأ ذاته الذي بُنيت عليه الطباعة: المتصفح يتولّى العربية
 * والاتجاه ثنائي الاتجاه، والمكتبات لا تتولاهما.
 */
function PdfViewer({ row, onClose }: { row: AttachmentRow; onClose: () => void }) {
  const { url, loading, error } = useAttachmentUrl(row.id, true);
  const frameRef = useRef<HTMLIFrameElement>(null);

  return (
    <Modal title={row.filename} onClose={onClose} wide>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="skeleton" style={{ height: 420 }} />}
      {url && (
        <>
          {/* ملء الشاشة على الإطار نفسه — والخروج بـEscape كما هو معهود. */}
          <div className="row mb-2" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn btn-sm"
              onClick={() => void frameRef.current?.requestFullscreen?.()}
              title="ملء الشاشة"
            >
              <Icon name="expand" size={14} />
              ملء الشاشة
            </button>
          </div>
          <iframe ref={frameRef} className="att-pdf" src={url} title={row.filename} />
        </>
      )}
    </Modal>
  );
}
